import { useState } from 'react';
import { useNavigate, useRevalidator, useSearchParams } from 'react-router-dom';

import usePlanChangePreview from '~/hooks/useLoaders/usePlanChangePreview';
import type { Site } from '~/interfaces/site';
import type { Subscription } from '~/interfaces/subscriptions';
import { useCurrentPlan } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useCurrentPlan';
import { hasGroupAndIndividualOverlap as detectGroupAndIndividualOverlap } from '~/routes/subscribe/$subscriberId/_utils/hasGroupAndIndividualOverlap';
import { isInvalidSubscription } from '~/utils/isInvalidSubscription';

import { SubscriptionActionsContext, type SubscriptionActionsContextType } from './SubscriptionActionsContext';
import { showUnsubscribeRetentionModal } from './showUnsubscribeRetentionModal';
import { useEffectiveGroupSubscriptionsContext } from './useEffectiveGroupSubscriptionsContext';
import { useModalCoordinationContext } from './useModalCoordinationContext';

type Props = {
  subscription: Subscription;
  site: Site;
  subscriberId: string;
  isLoggedIn: boolean;
  children: React.ReactNode;
};

/**
 * Owns the full subscription lifecycle surface — unsubscribe / resubscribe / report-abuse /
 * downgrade / change-tier / (un)pause — plus the current-plans display data and the single
 * tier-selection state every tier action shares. Self-calls `useCurrentPlan` (deduped with
 * General's billing call via the React-Query cache) and reads the scenario-applied group list from
 * `EffectiveGroupSubscriptionsProvider`, so it needs no derived props. Renders no UI; the colocated
 * consumer children render from context.
 */
export const SubscriptionActionsProvider = ({ subscription, site, subscriberId, isLoggedIn, children }: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state: revalidateState } = useRevalidator();
  const { effectiveGroupSubscriptions } = useEffectiveGroupSubscriptionsContext();
  const {
    local: { handleOpenModal, handleCloseModal, currentModal },
  } = useModalCoordinationContext();

  const publication = site.publication;
  const groupSubscriptionsEnabled = publication.group_subscriptions_enabled ?? false;
  const isRevalidating = revalidateState === 'loading';
  const tierIdInParams = searchParams.get('tier_id');

  const {
    isPaused,
    areAllPlansGifts,
    areAllPlansDowngrading,
    canUpgrade,
    canUnsubscribe,
    isInactive,
    isPremiumTier,
    hasActivePaidTier,
    showEmptyPlanState,
    unsubscribeMessage,
    resubscribeMessage,
    downgradeMessage,
    subscriptionPriceIds,
    subscriptionTierIds,
    currentPlans,
    isLoading: isLoadingCurrentPlan,
    refetchBillingSummary,
  } = useCurrentPlan({ isLoggedIn, site, subscription, subscriberId, groupSubscriptionsEnabled });

  const { preview: prorationPreview, isLoading: isLoadingProration, fetchPreview } = usePlanChangePreview();

  const { pause_info } = subscription || {};
  const isSubscriptionInvalid = isInvalidSubscription(subscription);
  const retentionFlowEnabled = Boolean(publication.retention_flow_enabled && isLoggedIn);
  const canShowCurrentPlans = (isLoggedIn || (!isLoggedIn && !hasActivePaidTier)) && !isSubscriptionInvalid;
  const hasGroupAndIndividualOverlap = detectGroupAndIndividualOverlap({
    groupSubscriptionsEnabled,
    currentPlans,
    groupSubscriptions: effectiveGroupSubscriptions,
  });

  // Single tier selection shared by downgrade / change-plan / unpause-to-tier.
  const [requestChangeInTierId, setRequestChangeInTierId] = useState<string | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);

  const setFirstPlanAsTierId = () => {
    if (currentPlans.length === 1 && currentPlans[0]?.id) {
      setRequestChangeInTierId(currentPlans[0].id);
    }
  };

  const openTierModal = (modal: 'downgrade' | 'change_plan' | 'unpause_plan') => (tierId?: string) => {
    if (tierId) {
      setRequestChangeInTierId(tierId);
    } else {
      setFirstPlanAsTierId();
    }
    handleOpenModal(modal);
  };

  const closeModal = () => {
    handleCloseModal();
    setRequestChangeInTierId(null);
  };

  const onSelectPrice = (priceId: string) => {
    setSelectedPriceId(priceId);
    fetchPreview(priceId);
    handleOpenModal('proration_confirm');
  };

  const onUnsubscribeSuccess = () => {
    closeModal();
    refetchBillingSummary();
  };

  const onUnpausePlanSuccess = () => {
    closeModal();
    refetchBillingSummary();
  };

  const onChangePlanSuccess = () => {
    closeModal();
    refetchBillingSummary();
  };

  const onDowngradeSuccess = () => {
    closeModal();
    if (publication.downgrade_form_id) {
      navigate(`/forms/${publication.downgrade_form_id}?email=${subscription.email}`);
    } else {
      refetchBillingSummary();
    }
  };

  const onClickResubscribe = isInactive ? () => handleOpenModal('resubscribe') : undefined;
  const onClickUnsubscribe = !isInactive && canUnsubscribe ? () => handleOpenModal('unsubscribe') : undefined;
  const onClickReportUnwantedSignup =
    subscription?.status === 'active' ? () => handleOpenModal('report_abuse') : undefined;
  const onClickUnpause = pause_info?.id ? () => handleOpenModal('unpause') : undefined;
  const onClickUpgrade = canUpgrade ? () => navigate(`/upgrade?email=${subscription.email}`) : undefined;
  const onClickLogin =
    !isLoggedIn && hasActivePaidTier ? () => handleOpenModal('login', { preventScrollReset: true }) : undefined;

  const actionableTier = currentPlans.find((tier) => tier.id === requestChangeInTierId)?.tier;
  const tierUnpausePauseInfoId = isLoggedIn
    ? subscription?.premium_tiers?.find((tier) => tier.id === requestChangeInTierId)?.pause_info?.id
    : undefined;

  const value: SubscriptionActionsContextType = {
    onClickUnsubscribe,
    onClickResubscribe,
    onClickReportUnwantedSignup,
    onClickUnpause,
    onClickUnpausePlan: openTierModal('unpause_plan'),
    onClickChangePlan: openTierModal('change_plan'),
    onClickCancelPlan: openTierModal('downgrade'),
    onClickUpgrade,
    onClickLogin,

    currentPlans,
    showEmptyPlanState,
    isLoadingPlans: isLoadingCurrentPlan || isRevalidating,
    canShowCurrentPlans,
    hasGroupAndIndividualOverlap,
    groupSubscriptionsEnabled,
    groupAccessRevokedFrom: subscription.group_access_revoked_from,

    currentModal,
    closeModal,
    publication: site.publication,
    subscriberId,
    isLoggedIn,

    retentionFlowEnabled,
    isPaused,
    isPremiumTier,
    hasActivePaidTier,
    areAllPlansGifts,
    areAllPlansDowngrading,
    unsubscribeMessage,
    showUnsubscribeRetentionModal: showUnsubscribeRetentionModal({
      retentionFlowEnabled,
      isPaused,
      isLoggedIn,
      isPremiumTier,
      areAllPlansDowngrading,
    }),
    resubscribeMessage,
    onUnsubscribeSuccess,

    accountPauseId: pause_info?.id,
    tierUnpauseTierId: requestChangeInTierId,
    tierUnpausePauseInfoId,
    onUnpausePlanSuccess,

    changePlanTierId: requestChangeInTierId ?? tierIdInParams,
    actionableTier,
    subscriptionPriceIds,
    subscriptionTierIds,
    selectedPriceId,
    onSelectPrice,
    prorationPreview,
    isLoadingProration,
    onChangePlanSuccess,

    downgradeMessage,
    onDowngradeSuccess,
  };

  return <SubscriptionActionsContext.Provider value={value}>{children}</SubscriptionActionsContext.Provider>;
};
